import React, { createContext, useContext, useState, useEffect, ReactNode, ComponentType, SVGProps } from "react";
import { getTenant } from "../theme";

export type SvgComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface TenantAssets {
  loaderIcon: SvgComponent;
  introBg: SvgComponent;
  calendarIcon: SvgComponent;
}

interface TenantAssetsContextValue {
  assets: TenantAssets | null;
  isLoading: boolean;
}

const TenantAssetsContext = createContext<TenantAssetsContextValue>({
  assets: null,
  isLoading: true,
});

export const useTenantAssets = () => useContext(TenantAssetsContext);

const tenantLoaders: Record<string, () => Promise<{ TenantAssets: TenantAssets }>> = {
  "healthy-kitchen": () => import(/* webpackChunkName: "tenant-healthy-kitchen" */ "../assets/icons/tenants/healthy-kitchen"),
  "second-bite": () => import(/* webpackChunkName: "tenant-second-bite" */ "../assets/icons/tenants/second-bite"),
  "diet-center": () => import(/* webpackChunkName: "tenant-diet-center" */ "../assets/icons/tenants/diet-center"),
  "low-cal": () => import(/* webpackChunkName: "tenant-low-cal" */ "../assets/icons/tenants/low-cal"),
  "high-protein": () => import(/* webpackChunkName: "tenant-high-protein" */ "../assets/icons/tenants/high-protein"),
  "rejeem": () => import(/* webpackChunkName: "tenant-rejeem" */ "../assets/icons/tenants/rejeem"),
};

const loadTenantAssets = async (tenantName: string): Promise<TenantAssets> => {
  const loader = tenantLoaders[tenantName] || tenantLoaders["rejeem"];
  const module = await loader();
  return module.TenantAssets;
};

interface TenantAssetsProviderProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export const TenantAssetsProvider: React.FC<TenantAssetsProviderProps> = ({ children, fallback }) => {
  const [assets, setAssets] = useState<TenantAssets | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const tenant = getTenant();
    loadTenantAssets(tenant.name)
      .then((loadedAssets) => {
        setAssets(loadedAssets);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load tenant assets:", error);
        setIsLoading(false);
      });
  }, []);

  if (isLoading && fallback) {
    return <>{fallback}</>;
  }

  return (
    <TenantAssetsContext.Provider value={{ assets, isLoading }}>
      {children}
    </TenantAssetsContext.Provider>
  );
};

export default TenantAssetsProvider;
